import { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useDialog } from '../../context/DialogContext'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import {
  UserPlus,
  CircleNotch,
  Crown,
  ShieldCheck,
  User as UserIcon,
  DotsThreeVertical,
  Envelope,
  Clock,
  XCircle,
  ArrowClockwise,
  Trash,
  CheckCircle,
  Warning,
} from '@phosphor-icons/react'
import {
  listCompanyUsers,
  listCompanyInvites,
  updateUserRole,
  updateUserStatus,
  removeUserFromCompany,
  cancelInvite,
  resendInvite,
  getCurrentUserRole,
  type CompanyUser,
  type CompanyInvite,
  type UserRole,
} from '../../services/company-users'
import { InviteUserModal } from './InviteUserModal'

type SubTab = 'members' | 'invites'

export function TeamTab() {
  const { user } = useAuth()
  const { confirm, toastSuccess, toastError } = useDialog()

  const [subTab, setSubTab] = useState<SubTab>('members')
  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState<CompanyUser[]>([])
  const [invites, setInvites] = useState<CompanyInvite[]>([])
  const [currentUserRole, setCurrentUserRole] = useState<UserRole>('user')
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null)

  const canManageUsers = currentUserRole === 'master' || currentUserRole === 'admin'
  const isMaster = currentUserRole === 'master'

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [usersRes, invitesRes, roleRes] = await Promise.all([
        listCompanyUsers(),
        listCompanyInvites(),
        getCurrentUserRole(),
      ])
      setUsers(usersRes.users)
      setInvites(invitesRes.invites)
      setCurrentUserRole(roleRes.role)
    } catch (error) {
      console.error('Error loading team data:', error)
      toastError('Erro', 'Erro ao carregar dados da equipe')
    } finally {
      setLoading(false)
    }
  }

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    try {
      await updateUserRole(userId, newRole)
      toastSuccess('Sucesso', 'Role atualizado com sucesso')
      loadData()
    } catch (error: any) {
      toastError('Erro', error.response?.data?.error || 'Erro ao atualizar role')
    }
    setActionMenuOpen(null)
  }

  const handleStatusToggle = async (userId: string, currentStatus: boolean) => {
    const action = currentStatus ? 'desativar' : 'ativar'
    const confirmed = await confirm({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Usuário`,
      message: `Tem certeza que deseja ${action} este usuário?`,
    })
    if (!confirmed) return

    try {
      await updateUserStatus(userId, !currentStatus)
      toastSuccess('Sucesso', `Usuário ${currentStatus ? 'desativado' : 'ativado'} com sucesso`)
      loadData()
    } catch (error: any) {
      toastError('Erro', error.response?.data?.error || 'Erro ao alterar status')
    }
    setActionMenuOpen(null)
  }

  const handleRemoveUser = async (userId: string, userName: string) => {
    const confirmed = await confirm({
      title: 'Remover Usuário',
      message: `Tem certeza que deseja remover ${userName} da empresa? O usuário ainda poderá acessar outras empresas que pertence.`,
      variant: 'danger',
    })
    if (!confirmed) return

    try {
      await removeUserFromCompany(userId)
      toastSuccess('Sucesso', 'Usuário removido da empresa')
      loadData()
    } catch (error: any) {
      toastError('Erro', error.response?.data?.error || 'Erro ao remover usuário')
    }
    setActionMenuOpen(null)
  }

  const handleCancelInvite = async (inviteId: string) => {
    const confirmed = await confirm({
      title: 'Cancelar Convite',
      message: 'Tem certeza que deseja cancelar este convite?',
      variant: 'warning',
    })
    if (!confirmed) return

    try {
      await cancelInvite(inviteId)
      toastSuccess('Sucesso', 'Convite cancelado com sucesso')
      loadData()
    } catch (error: any) {
      toastError('Erro', error.response?.data?.error || 'Erro ao cancelar convite')
    }
  }

  const handleResendInvite = async (inviteId: string) => {
    try {
      await resendInvite(inviteId)
      toastSuccess('Sucesso', 'Convite reenviado com sucesso')
    } catch (error: any) {
      toastError('Erro', error.response?.data?.error || 'Erro ao reenviar convite')
    }
  }

  const getRoleIcon = (role: UserRole) => {
    switch (role) {
      case 'master':
        return <Crown size={16} className="text-yellow-500" weight="fill" />
      case 'admin':
        return <ShieldCheck size={16} className="text-blue-500" weight="fill" />
      default:
        return <UserIcon size={16} className="text-gray-400" />
    }
  }

  const getRoleLabel = (role: UserRole) => {
    switch (role) {
      case 'master':
        return 'Master'
      case 'admin':
        return 'Admin'
      default:
        return 'Usuário'
    }
  }

  const getRoleBadgeClass = (role: UserRole) => {
    switch (role) {
      case 'master':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200'
      case 'admin':
        return 'bg-blue-100 text-blue-700 border-blue-200'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase()
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <CircleNotch size={32} className="animate-spin text-emerald-600" />
        </CardContent>
      </Card>
    )
  }

  const activeUsers = users.filter((u) => u.is_active)
  const inactiveUsers = users.filter((u) => !u.is_active)

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Equipe</CardTitle>
          {canManageUsers && (
            <Button
              onClick={() => setShowInviteModal(true)}
              className="gap-2"
              style={{ backgroundColor: 'rgb(5, 150, 105)' }}
            >
              <UserPlus size={20} />
              Convidar Usuário
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {/* Sub tabs */}
          <div className="flex gap-2 mb-6 border-b border-gray-200">
            <button
              onClick={() => setSubTab('members')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                subTab === 'members'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Membros ({activeUsers.length})
            </button>
            <button
              onClick={() => setSubTab('invites')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                subTab === 'invites'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Convites Pendentes ({invites.length})
            </button>
          </div>

          {/* Members list */}
          {subTab === 'members' && (
            <div className="space-y-3">
              {activeUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nenhum membro ativo na equipe
                </div>
              ) : (
                activeUsers.map((member) => (
                  <div
                    key={member.membership_id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100"
                  >
                    <div className="flex items-center gap-4">
                      {/* Avatar */}
                      <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center overflow-hidden">
                        {member.avatar_url ? (
                          <img
                            src={member.avatar_url}
                            alt={member.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-sm font-semibold text-emerald-600">
                            {getInitials(member.name)}
                          </span>
                        )}
                      </div>

                      {/* Info */}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{member.name}</span>
                          {member.user_id === user?.id && (
                            <span className="text-xs text-gray-400">(você)</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Envelope size={14} />
                          {member.email}
                        </div>
                        {(member.cargo || member.department) && (
                          <div className="text-xs text-gray-400 mt-1">
                            {member.cargo}
                            {member.cargo && member.department && ' • '}
                            {member.department}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {/* Role badge */}
                      <div
                        className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${getRoleBadgeClass(
                          member.role
                        )}`}
                      >
                        {getRoleIcon(member.role)}
                        {getRoleLabel(member.role)}
                      </div>

                      {/* Status */}
                      <div
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                          member.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {member.is_active ? (
                          <>
                            <CheckCircle size={12} weight="fill" />
                            Ativo
                          </>
                        ) : (
                          <>
                            <XCircle size={12} weight="fill" />
                            Inativo
                          </>
                        )}
                      </div>

                      {/* Actions menu */}
                      {canManageUsers && member.user_id !== user?.id && (
                        <div className="relative">
                          <button
                            onClick={() =>
                              setActionMenuOpen(
                                actionMenuOpen === member.user_id ? null : member.user_id
                              )
                            }
                            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                          >
                            <DotsThreeVertical size={20} className="text-gray-500" />
                          </button>

                          {actionMenuOpen === member.user_id && (
                            <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                              {/* Role change options */}
                              {isMaster && member.role !== 'master' && (
                                <button
                                  onClick={() => handleRoleChange(member.user_id, 'master')}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                                >
                                  <Crown size={16} className="text-yellow-500" />
                                  Promover a Master
                                </button>
                              )}
                              {member.role !== 'admin' && (
                                <button
                                  onClick={() => handleRoleChange(member.user_id, 'admin')}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                                >
                                  <ShieldCheck size={16} className="text-blue-500" />
                                  {member.role === 'master' ? 'Rebaixar para Admin' : 'Promover a Admin'}
                                </button>
                              )}
                              {member.role !== 'user' && (
                                <button
                                  onClick={() => handleRoleChange(member.user_id, 'user')}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                                >
                                  <UserIcon size={16} className="text-gray-400" />
                                  Rebaixar para Usuário
                                </button>
                              )}

                              <div className="border-t border-gray-100 my-1" />

                              {/* Status toggle */}
                              <button
                                onClick={() => handleStatusToggle(member.user_id, member.is_active)}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                              >
                                {member.is_active ? (
                                  <>
                                    <XCircle size={16} className="text-orange-500" />
                                    Desativar usuário
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle size={16} className="text-green-500" />
                                    Ativar usuário
                                  </>
                                )}
                              </button>

                              {/* Remove */}
                              <button
                                onClick={() => handleRemoveUser(member.user_id, member.name)}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
                              >
                                <Trash size={16} />
                                Remover da empresa
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}

              {/* Inactive users section */}
              {inactiveUsers.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                    <Warning size={16} />
                    Usuários Inativos ({inactiveUsers.length})
                  </h4>
                  {inactiveUsers.map((member) => (
                    <div
                      key={member.membership_id}
                      className="flex items-center justify-between p-4 bg-gray-100 rounded-lg border border-gray-200 opacity-70"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-sm font-semibold text-gray-400">
                            {getInitials(member.name)}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">{member.name}</span>
                          <div className="text-sm text-gray-400">{member.email}</div>
                        </div>
                      </div>

                      {canManageUsers && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusToggle(member.user_id, member.is_active)}
                        >
                          <CheckCircle size={16} className="mr-2" />
                          Reativar
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Invites list */}
          {subTab === 'invites' && (
            <div className="space-y-3">
              {invites.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nenhum convite pendente
                </div>
              ) : (
                invites.map((invite) => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-100"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                        <Envelope size={20} className="text-amber-600" />
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">{invite.email}</span>
                        <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeClass(
                              invite.role
                            )}`}
                          >
                            {getRoleLabel(invite.role)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            Expira em {formatDate(invite.expires_at)}
                          </span>
                        </div>
                        {invite.invited_by_name && (
                          <div className="text-xs text-gray-400 mt-1">
                            Convidado por {invite.invited_by_name}
                          </div>
                        )}
                      </div>
                    </div>

                    {canManageUsers && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleResendInvite(invite.id)}
                        >
                          <ArrowClockwise size={16} className="mr-2" />
                          Reenviar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCancelInvite(invite.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <XCircle size={16} className="mr-2" />
                          Cancelar
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invite Modal */}
      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={() => {
          setShowInviteModal(false)
          loadData()
        }}
        currentUserRole={currentUserRole}
      />

      {/* Click outside to close action menu */}
      {actionMenuOpen && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setActionMenuOpen(null)}
        />
      )}
    </>
  )
}
